import React, { useMemo, useState } from "react";
import { DescriptionText } from "../../styles/profile/form.style";
import { GridHeaderCell, ScheduleCell, ScheduleGrid, ScheduleKeyColorBox, ScheduleKeyContainer, ScheduleKeyItem, ScheduleKeyLabel, TimeHeaderCell } from "../../styles/search/search.style";
import { UserDetails } from "../../types/schemas/abc";
import { useAvailability } from "../../hooks/useAvailability";
import { getTimezoneOffset, adjustAvailabilityPerDay, GRANULARITY, DAYS_MONDAY_FIRST } from "../../utils/availability";
import { getDetectedTimezone, shortTzName, friendlyTzName } from "../../utils/timezones";
import { Globe } from 'lucide-react';


interface ScheduleViewProps { 
    tutor: UserDetails; 
    parent: UserDetails;
    currentUserRole: 'parent' | 'tutor'; 
}

export const ScheduleView: React.FC<ScheduleViewProps> = ({tutor, parent, currentUserRole}) => {
    let user1: UserDetails;
    let user2: UserDetails;
    let counterPartyRole: string;

    if (currentUserRole === 'parent') {
      user1 = parent;
      user2 = tutor;
      counterPartyRole = "Tutor's";
    } else {
      user1 = tutor;
      user2 = parent;
      counterPartyRole = "Parent's";
    }

    const browserTz = useMemo(() => getDetectedTimezone(), []);
    const homeTz = user1?.timezone || 'Europe/London';
    const cpTz = user2?.timezone;

    // Build merged tz entries
    const tzEntries = useMemo(() => {
      const tzMap = new Map<string, { key: string; parts: string[] }>();
      const homeEntry = tzMap.get(homeTz) || { key: 'home', parts: [] };
      homeEntry.parts.push('Home');
      tzMap.set(homeTz, homeEntry);

      const browserEntry = tzMap.get(browserTz) || { key: 'browser', parts: [] };
      browserEntry.parts.push('Current');
      tzMap.set(browserTz, browserEntry);

      if (cpTz) {
        const cpEntry = tzMap.get(cpTz) || { key: 'counterparty', parts: [] };
        cpEntry.parts.push(counterPartyRole);
        tzMap.set(cpTz, cpEntry);
      }

      return Array.from(tzMap.entries()).map(([tz, { key, parts }]) => ({
        key, tz,
        label: `${parts.join(' / ')} timezone (${friendlyTzName(tz)})`,
      }));
    }, [homeTz, browserTz, cpTz, counterPartyRole]);

    const [activeTzKey, setActiveTzKey] = useState('home');
    const activeTz = tzEntries.find(e => e.key === activeTzKey)?.tz || homeTz;
    const tzSwitchButtons = tzEntries.filter(e => e.key !== activeTzKey);

    // Build per-day offsets for DST-accurate shifting (Mon=0..Sun=6 in DAYS_MONDAY_FIRST)
    const buildDayOffsets = (fromTz: string, toTz: string): number[] => {
      const now = new Date();
      const todayMon = (now.getDay() + 6) % 7; // 0=Mon
      return DAYS_MONDAY_FIRST.map((_, i) => {
        const d = new Date(now);
        d.setDate(d.getDate() + (i - todayMon));
        return getTimezoneOffset(toTz, fromTz, d);
      });
    };

    // Pre-shift both users' availability into activeTz using per-day offsets
    const user1AvailInActiveTz = useMemo(() => {
      if (!user1?.availability || activeTz === homeTz) return user1?.availability;
      return adjustAvailabilityPerDay(user1.availability, buildDayOffsets(homeTz, activeTz!), DAYS_MONDAY_FIRST);
    }, [user1?.availability, activeTz, homeTz]);

    const user2AvailInActiveTz = useMemo(() => {
      if (!user2?.availability || !user2?.timezone || activeTz === user2.timezone) return user2?.availability;
      return adjustAvailabilityPerDay(user2.availability, buildDayOffsets(user2.timezone, activeTz!), DAYS_MONDAY_FIRST);
    }, [user2?.availability, user2?.timezone, activeTz]);

    const {availabilityStatusMap, hoursOfDay} = useAvailability(
      {availability: user1AvailInActiveTz, timezone: activeTz},
      {availability: user2AvailInActiveTz, timezone: activeTz}
    );

    const timezoneNote = useMemo(() => {
      if (!activeTz || !user2.timezone) return null;
      if (activeTz === user2.timezone) return `Availability overlap shown in ${friendlyTzName(activeTz)}.`;
      return `${counterPartyRole} timezone: ${friendlyTzName(user2.timezone)}. Shown in your time (${friendlyTzName(activeTz)}).`;
    }, [activeTz, user2.timezone, counterPartyRole]);

    const daysOfWeek = [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ];

    return (
    <div>
    {tzSwitchButtons.length > 0 && (
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        {tzSwitchButtons.map(btn => (
          <button
            key={btn.key}
            onClick={() => setActiveTzKey(btn.key)}
            style={{
              all: 'unset',
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.3rem 0.65rem',
              borderRadius: '0.5rem',
              fontSize: '0.75rem',
              fontWeight: 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              border: '1px solid var(--colors-sand7, #d4d4d4)',
              color: 'var(--colors-sand12, #1a1a1a)',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--colors-sand4, #f0f0f0)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <Globe size={13} />
            View in {btn.label}
          </button>
        ))}
      </div>
    )}

    {availabilityStatusMap.size > 0 && (
            <ScheduleKeyContainer>
              <ScheduleKeyItem>
                <ScheduleKeyColorBox color="user1Only" />
                <ScheduleKeyLabel>Your Availability</ScheduleKeyLabel>
              </ScheduleKeyItem>
              <ScheduleKeyItem>
                <ScheduleKeyColorBox color="user2Only" />
                <ScheduleKeyLabel>{counterPartyRole}'s Availability</ScheduleKeyLabel>
              </ScheduleKeyItem>
              <ScheduleKeyItem>
                <ScheduleKeyColorBox color="overlap" />
                <ScheduleKeyLabel>Overlap</ScheduleKeyLabel>
              </ScheduleKeyItem>
            </ScheduleKeyContainer>
          )}

          {availabilityStatusMap.size > 0 ? (
            <ScheduleGrid css={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '60dvh' }}>
              <GridHeaderCell />
              {daysOfWeek.map((day) => (
                <GridHeaderCell key={day}>{day}</GridHeaderCell>
              ))}

              {hoursOfDay.map((minute) => (
                <React.Fragment key={minute}>
                  <TimeHeaderCell>
                    {new Date(0, 0, 0, 0, minute).toLocaleTimeString([], {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </TimeHeaderCell>

                  {daysOfWeek.map((day) => {
                    const key = `${day.toLowerCase()}-${minute}`;
                    const status = availabilityStatusMap.get(key) || 'empty';

                    return <ScheduleCell key={key} status={status as any} />;
                  })}
                </React.Fragment>
              ))}
            </ScheduleGrid>
          ) : (
            <DescriptionText className={'not-provided'}>
              No availability information found to compare.
            </DescriptionText>
          )}

          {timezoneNote && availabilityStatusMap.size > 0 && (
            <p style={{
              margin: '0.6rem 0 0',
              fontSize: '0.75rem',
              color: '#065f46',
              fontStyle: 'italic',
            }}>
              {timezoneNote}
            </p>
          )}
    </div>
    );
    };